"use client";

import { useState } from "react";
import {
  DataTable,
  Notice,
  type Option,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { CurrencyField, useMoney } from "@/components/tools/finance/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  fromCostMargin,
  fromCostMarkup,
  fromCostPrice,
  fromPriceMargin,
  fromPriceMarkup,
  MARKUP_MODES,
  type MarkupMode,
  type MarkupResult,
  marginTable,
  marginToMarkup,
  markupTable,
  markupToMargin,
  scaleResult,
} from "@/lib/tool-kit/accounting/markup";

/** English strings. A translated content file overrides these through `ui`. */
export const MARKUP_MARGIN_CALCULATOR_STRINGS = {
  inputsHeading: "What you know",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  modeLabel: "Work out",
  modeCostPrice: "Markup and margin from cost and price",
  modeCostMarkup: "Price and margin from cost and markup",
  modeCostMargin: "Price and markup from cost and margin",
  modePriceMarkup: "Cost and margin from price and markup",
  modePriceMargin: "Cost and markup from price and margin",
  modeMarkupToMargin: "Convert a markup to a margin",
  modeMarginToMarkup: "Convert a margin to a markup",
  cost: "Cost per unit",
  price: "Selling price per unit",
  markupPercent: "Markup (% of cost)",
  marginPercent: "Margin (% of selling price)",
  units: "Units (for totals)",
  unitsHint: "Leave at 1 for a single item.",
  convention:
    "Markup is profit ÷ cost; margin is profit ÷ selling price. Margin is always the smaller of the two for a profit, and it can never reach 100%. Enter percentages without the % sign.",
  resultsHeading: "Results",
  resultCost: "Cost",
  resultPrice: "Selling price",
  resultProfit: "Profit",
  resultLoss: "Loss",
  resultMarkup: "Markup",
  resultMargin: "Margin",
  workingHeading: "The working",
  workProfit: "Profit = price − cost = {price} − {cost} = {profit}",
  workMarkup: "Markup = profit ÷ cost = {profit} ÷ {cost} = {markup}",
  workMargin: "Margin = profit ÷ price = {profit} ÷ {price} = {margin}",
  workPriceMarkup:
    "Price = cost × (1 + markup) = {cost} × (1 + {markup}) = {price}",
  workPriceMargin:
    "Price = cost ÷ (1 − margin) = {cost} ÷ (1 − {margin}) = {price}",
  workCostMarkup:
    "Cost = price ÷ (1 + markup) = {price} ÷ (1 + {markup}) = {cost}",
  workCostMargin:
    "Cost = price × (1 − margin) = {price} × (1 − {margin}) = {cost}",
  workToMargin:
    "Margin = markup ÷ (1 + markup) = {markup} ÷ (1 + {markup}) = {margin}",
  workToMarkup:
    "Markup = margin ÷ (1 − margin) = {margin} ÷ (1 − {margin}) = {markup}",
  tableHeading: "Per unit and in total",
  itemColumn: "Item",
  perUnitColumn: "Per unit",
  totalColumn: "one:Total for {units} unit|other:Total for {units} units",
  illustration:
    "In plain terms: an item that costs 100 sells for {price}, a profit of {profit}.",
  belowCost:
    "The selling price is below cost, so the markup and margin are negative — this is a loss.",
  referenceMarginHeading: "The markup that gives a target margin",
  referenceMarkupHeading: "The margin that a markup gives",
  marginColumn: "Margin",
  markupColumn: "Markup",
  needValid:
    "Check the numbers: costs and prices must be above zero, a margin must be below 100% and a markup above −100%.",
} as const;

type Strings = typeof MARKUP_MARGIN_CALCULATOR_STRINGS;
type Kind = "money" | "percent";

interface FieldSpec {
  label: keyof Strings;
  kind: Kind;
}

interface ModeSpec {
  label: keyof Strings;
  first: FieldSpec;
  second: FieldSpec | null;
  defaults: [string, string];
  /** Working lines, in order. */
  lines: (keyof Strings)[];
}

const COST: FieldSpec = { label: "cost", kind: "money" };
const PRICE: FieldSpec = { label: "price", kind: "money" };
const MARKUP: FieldSpec = { label: "markupPercent", kind: "percent" };
const MARGIN: FieldSpec = { label: "marginPercent", kind: "percent" };

const MODES: Record<MarkupMode, ModeSpec> = {
  "cost-price": {
    label: "modeCostPrice",
    first: COST,
    second: PRICE,
    defaults: ["60", "100"],
    lines: ["workProfit", "workMarkup", "workMargin"],
  },
  "cost-markup": {
    label: "modeCostMarkup",
    first: COST,
    second: MARKUP,
    defaults: ["60", "50"],
    lines: ["workPriceMarkup", "workProfit", "workMargin"],
  },
  "cost-margin": {
    label: "modeCostMargin",
    first: COST,
    second: MARGIN,
    defaults: ["60", "40"],
    lines: ["workPriceMargin", "workProfit", "workMarkup"],
  },
  "price-markup": {
    label: "modePriceMarkup",
    first: PRICE,
    second: MARKUP,
    defaults: ["150", "50"],
    lines: ["workCostMarkup", "workProfit", "workMargin"],
  },
  "price-margin": {
    label: "modePriceMargin",
    first: PRICE,
    second: MARGIN,
    defaults: ["100", "40"],
    lines: ["workCostMargin", "workProfit", "workMarkup"],
  },
  "markup-to-margin": {
    label: "modeMarkupToMargin",
    first: MARKUP,
    second: null,
    defaults: ["50", ""],
    lines: ["workToMargin"],
  },
  "margin-to-markup": {
    label: "modeMarginToMarkup",
    first: MARGIN,
    second: null,
    defaults: ["40", ""],
    lines: ["workToMarkup"],
  },
};

type Entries = Record<MarkupMode, [string, string]>;

function initialEntries(): Entries {
  const entries = {} as Entries;
  for (const mode of MARKUP_MODES) {
    entries[mode] = [...MODES[mode].defaults];
  }
  return entries;
}

/** Percent inputs arrive as 25 for 25%; money stays as typed. */
function readValue(text: string, kind: Kind): number | null {
  const value = parseNumberInput(text);
  if (value === null) {
    return null;
  }
  return kind === "percent" ? value / 100 : value;
}

interface Solved {
  /** Null in the two conversion modes, which have no amounts. */
  amounts: MarkupResult | null;
  markup: number;
  margin: number;
}

function solve(mode: MarkupMode, a: number, b: number): Solved | null {
  let amounts: MarkupResult | null = null;
  if (mode === "markup-to-margin") {
    const margin = markupToMargin(a);
    return margin === null ? null : { amounts, markup: a, margin };
  }
  if (mode === "margin-to-markup") {
    const markup = marginToMarkup(a);
    return markup === null ? null : { amounts, markup, margin: a };
  }
  if (mode === "cost-price") {
    amounts = fromCostPrice(a, b);
  } else if (mode === "cost-markup") {
    amounts = fromCostMarkup(a, b);
  } else if (mode === "cost-margin") {
    amounts = fromCostMargin(a, b);
  } else if (mode === "price-markup") {
    amounts = fromPriceMarkup(a, b);
  } else {
    amounts = fromPriceMargin(a, b);
  }
  return amounts
    ? { amounts, markup: amounts.markup, margin: amounts.margin }
    : null;
}

function Working({
  mode,
  solved,
  currency,
  strings,
}: {
  mode: MarkupMode;
  solved: Solved;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const pct = (v: number) => `${fmt(v * 100, { maximumFractionDigits: 2 })}%`;
  const r = solved.amounts;
  const values = {
    cost: r ? money(r.cost) : "",
    price: r ? money(r.price) : "",
    profit: r ? money(r.profit) : "",
    markup: pct(solved.markup),
    margin: pct(solved.margin),
  };
  return (
    <div className={`${CARD} space-y-1`}>
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      {MODES[mode].lines.map((key) => (
        <p
          className="font-mono text-muted-foreground text-sm leading-relaxed"
          key={key}
        >
          {t(strings[key], values)}
        </p>
      ))}
    </div>
  );
}

function Amounts({
  amounts,
  units,
  currency,
  strings,
}: {
  amounts: MarkupResult;
  units: number;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const money = useMoney(currency);
  const total = scaleResult(amounts, units);
  const profitLabel =
    amounts.profit < 0 ? strings.resultLoss : strings.resultProfit;
  const rows = [
    [strings.resultCost, money(amounts.cost), money(total.cost)],
    [strings.resultPrice, money(amounts.price), money(total.price)],
    [profitLabel, money(amounts.profit), money(total.profit)],
  ];
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.itemColumn,
          strings.perUnitColumn,
          tPlural(strings.totalColumn, locale, units, { units: fmt(units) }),
        ]}
        rows={rows}
      />
    </>
  );
}

function Reference({ strings }: { strings: Strings }) {
  const fmt = useToolNumber();
  const pct = (v: number) => `${fmt(v * 100, { maximumFractionDigits: 2 })}%`;
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.referenceMarginHeading}</h3>
      <DataTable
        head={[strings.marginColumn, strings.markupColumn]}
        rows={marginTable().map((row) => [pct(row.from), pct(row.to)])}
      />
      <h3 className={WIDGET_H3}>{strings.referenceMarkupHeading}</h3>
      <DataTable
        head={[strings.markupColumn, strings.marginColumn]}
        rows={markupTable().map((row) => [pct(row.from), pct(row.to)])}
      />
    </>
  );
}

function Results({
  mode,
  solved,
  units,
  currency,
  strings,
}: {
  mode: MarkupMode;
  solved: Solved;
  units: number;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const pct = (v: number) => `${fmt(v * 100, { maximumFractionDigits: 2 })}%`;
  const r = solved.amounts;
  const emphasis = {
    "cost-price": "markup",
    "cost-markup": "price",
    "cost-margin": "price",
    "price-markup": "cost",
    "price-margin": "cost",
    "markup-to-margin": "margin",
    "margin-to-markup": "markup",
  }[mode];
  const hundred = priceAtCost100(solved.markup);
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        {r ? (
          <StatCard
            emphasis={emphasis === "price"}
            label={strings.resultPrice}
            value={money(r.price)}
          />
        ) : null}
        {r ? (
          <StatCard
            emphasis={emphasis === "cost"}
            label={strings.resultCost}
            value={money(r.cost)}
          />
        ) : null}
        {r ? (
          <StatCard
            label={r.profit < 0 ? strings.resultLoss : strings.resultProfit}
            value={money(Math.abs(r.profit))}
          />
        ) : null}
        <StatCard
          emphasis={emphasis === "markup"}
          label={strings.resultMarkup}
          value={pct(solved.markup)}
        />
        <StatCard
          emphasis={emphasis === "margin"}
          label={strings.resultMargin}
          value={pct(solved.margin)}
        />
      </StatGrid>
      {solved.markup < 0 ? <Notice>{strings.belowCost}</Notice> : null}
      <Working
        currency={currency}
        mode={mode}
        solved={solved}
        strings={strings}
      />
      {r ? (
        <Amounts
          amounts={r}
          currency={currency}
          strings={strings}
          units={units}
        />
      ) : (
        <p className={HINT}>
          {t(strings.illustration, {
            price: fmt(hundred, { maximumFractionDigits: 2 }),
            profit: fmt(hundred - 100, { maximumFractionDigits: 2 }),
          })}
        </p>
      )}
      <Reference strings={strings} />
    </section>
  );
}

/** The selling price of an item that costs 100 at this markup. */
function priceAtCost100(markup: number): number {
  return 100 * (1 + markup);
}

export default function MarkupMarginCalculatorWidget() {
  const strings = useToolUi(MARKUP_MARGIN_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [mode, setMode] = useState<MarkupMode>("cost-price");
  const [entries, setEntries] = useState<Entries>(initialEntries);
  const [unitsText, setUnitsText] = useState("1");

  const spec = MODES[mode];
  const setEntry = (index: 0 | 1, value: string) =>
    setEntries((old) => {
      const next: [string, string] = [...old[mode]];
      next[index] = value;
      return { ...old, [mode]: next };
    });
  const options: Option<MarkupMode>[] = MARKUP_MODES.map((key) => ({
    value: key,
    label: strings[MODES[key].label],
  }));

  const a = readValue(entries[mode][0], spec.first.kind);
  const b = spec.second ? readValue(entries[mode][1], spec.second.kind) : 0;
  const units = parseNumberInput(unitsText);
  const solved =
    a !== null && b !== null && units !== null && units > 0
      ? solve(mode, a, b)
      : null;

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <SelectField
          id="mm-mode"
          label={strings.modeLabel}
          onChange={setMode}
          options={options}
          value={mode}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyField
            id="mm-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <TextField
            id="mm-first"
            label={strings[spec.first.label]}
            numeric
            onChange={(value) => setEntry(0, value)}
            value={entries[mode][0]}
          />
          {spec.second ? (
            <TextField
              id="mm-second"
              label={strings[spec.second.label]}
              numeric
              onChange={(value) => setEntry(1, value)}
              value={entries[mode][1]}
            />
          ) : null}
          {spec.second ? (
            <TextField
              hint={strings.unitsHint}
              id="mm-units"
              label={strings.units}
              numeric
              onChange={setUnitsText}
              value={unitsText}
            />
          ) : null}
        </div>
        <p className={HINT}>{strings.convention}</p>
      </section>
      {solved && units !== null ? (
        <Results
          currency={currency}
          mode={mode}
          solved={solved}
          strings={strings}
          units={units}
        />
      ) : (
        <Notice>{strings.needValid}</Notice>
      )}
    </div>
  );
}
